import { ThemeProvider } from '@mui/material';
import { createContext, useContext, useEffect } from 'react';
import type { ReactNode } from 'react';
import { isFeatureReleased } from '@app/shared';
import { useReleaseState } from '../release';
import { brandColours, brandTheme, classicColours, theme } from './theme';
import type { ShellColours } from './theme';

const ColoursContext = createContext<ShellColours>(classicColours);

/** The frame's colours: 212Ez's branding while `brand-look` is on, the reference's otherwise. */
export const useShellColours = () => useContext(ColoursContext);

/** Is the switch `brand-look` on? Off while the switches load, so the page never flashes it. */
export function useBrandLook(): boolean {
  const { data } = useReleaseState();
  return !!data && isFeatureReleased(data, 'brand-look');
}

/** Gives the app the theme and frame colours of the branding switch. */
export function BrandThemeProvider({ children }: { children: ReactNode }) {
  const { data, isPending, failureCount } = useReleaseState();
  const on = !!data && isFeatureReleased(data, 'brand-look');
  // The tab icon follows the switch too: the name alone, coloured as in the name block.
  useEffect(() => {
    const icon = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (icon) icon.href = on ? '/favicon-name.svg' : '/favicon.svg';
  }, [on]);
  // Nothing is drawn until the switches answer (or the first try fails), so a page never shows
  // one look and then the other.
  if (isPending && failureCount === 0) return null;
  return (
    <ThemeProvider theme={on ? brandTheme : theme}>
      <ColoursContext.Provider value={on ? brandColours : classicColours}>
        {children}
      </ColoursContext.Provider>
    </ThemeProvider>
  );
}
